<script lang="ts">
  import { preventDefault, run } from "svelte/legacy";
  import CommandPalette from "./CommandPalette.svelte";
  import FileList from "./FileList.svelte";
  import { FileInfo, FS, FSDirState, fsFullDir } from "./fs";
  import { connectBySignIn, fsTypeNeedsSignIn, openSavedLocalFolder, signInFsName } from "./fs-make";
  import { captureEvent } from "./http";
  import Close from "./icons/Close.svelte";
  import { currentMessage, showMessage, showTemporaryMessage } from "./message";
  import type { NavHistory, PaneNav } from "./nav-history";
  import { navBack, navCanBack, navCanForward, navForward, navNew, navPush } from "./nav-history";
  import type { PaletteItem, PaletteLists } from "./palette";
  import { htmlLabelToText, isPaletteShortcut, palettePrefixOfKey } from "./palette";
  import PickFileSystem from "./PickFileSystem.svelte";
  import {
    addBookmark,
    bookmarkExists,
    bookmarkForFS,
    bookmarkFsType,
    bookmarkLabel,
    bookmarks,
    findBookmarkFS,
    findBookmarkSavedLocal,
    rememberFileSystem,
    removeBookmark,
  } from "./state.svelte";
  import { canCopyMove, viewportWidth } from "./store";
  import Tooltip from "./Tooltip.svelte";
  import { throwIf } from "./util";

  let fs1State = $state<FSDirState>();
  let fs1Component = $state<FileList>();
  let fs2State = $state<FSDirState>();
  let fs2Component = $state<FileList>();

  {
    let files = [];
    {
      let fi = new FileInfo();
      fi.name = "test.txt";
      fi.size = 2341234;
      files.push(fi);
    }
    {
      let fi = new FileInfo();
      fi.name = "long file name.pdf";
      fi.size = 23234;
      files.push(fi);
    }
    {
      let fi = new FileInfo();
      fi.name = "another.file.zip";
      fi.size = 88234;
      files.push(fi);
    }
    // entriesToCopy = files;
  }

  // the pane whose Home View takes the focus when it shows: the one that
  // went up from a file list, so the keys keep working in it
  let focusHomeView = $state(0);

  // A place a pane has shown: a directory of a file system, or the Home View
  // when fs is null.
  interface NavLoc {
    fs: FS | null;
    dir: string;
  }

  function navLocEq(a: NavLoc, b: NavLoc): boolean {
    if (!a.fs || !b.fs) {
      return a.fs == b.fs;
    }
    return fsEq(a.fs, b.fs) && a.dir == b.dir;
  }

  // both panes start in the Home View
  let history1: NavHistory<NavLoc> = $state(navNew({ fs: null, dir: "" }));
  let history2: NavHistory<NavLoc> = $state(navNew({ fs: null, dir: "" }));

  function paneHistory(num: number): NavHistory<NavLoc> {
    return num == 1 ? history1 : history2;
  }

  // remembers that the pane now shows a place
  function navVisited(num: number, fs: FS | null, dir: string) {
    navPush(paneHistory(num), { fs, dir }, navLocEq);
  }

  // shows a place of the history in the pane. The history already points at
  // it, so getting there is not remembered as a new place.
  function navShow(num: number, loc: NavLoc | null) {
    if (!loc) {
      return;
    }
    let state = num == 1 ? fs1State : fs2State;
    if (!loc.fs) {
      focusHomeView = num;
      if (num == 1) {
        fs1State = undefined;
      } else {
        fs2State = undefined;
      }
      return;
    }
    if (state && fsEq(state.fs, loc.fs)) {
      let component = num == 1 ? fs1Component : fs2Component;
      component?.tryReadDir(loc.dir);
      return;
    }
    let newState = new FSDirState(loc.fs, loc.dir);
    if (num == 1) {
      fs1State = newState;
    } else {
      fs2State = newState;
    }
  }

  function paneNav(num: number): PaneNav {
    let h = paneHistory(num);
    return {
      goback: navCanBack(h) ? () => navShow(num, navBack(h)) : undefined,
      goforward: navCanForward(h) ? () => navShow(num, navForward(h)) : undefined,
    };
  }
  let nav1 = $derived(paneNav(1));
  let nav2 = $derived(paneNav(2));

  function showTopLevel(num: number) {
    console.log("showTopLevel", num);
    focusHomeView = num;
    navVisited(num, null, "");
    if (num == 1) {
      fs1State = undefined;
    } else if (num == 2) {
      fs2State = undefined;
    }
  }

  /**
   * return true if bookmark represents the same location as filesystem
   */
  export function fsEq(fs1: FS, fs2: FS) {
    return fs1.config.fsID == fs2.config.fsID;
  }

  function calcCanCopyMove(fs1State: FSDirState, fs2State: FSDirState) {
    if (!fs1State || !fs2State) {
      return false;
    }
    if (!fs1State.isValidDir || !fs2State.isValidDir) {
      return false;
    }
    // disallow same directories
    if (fsEq(fs1State.fs, fs2State.fs)) {
      if (fs1State.currDir == fs2State.currDir) {
        return false;
      }
    }
    return true;
  }

  async function goToBookmark(b: import("./bookmarks").Bookmark, num: number) {
    console.log("goToBookmark:", b, num);

    let newFS = findBookmarkFS(b);
    let fsType = bookmarkFsType(b);
    if (!newFS && fsType && fsTypeNeedsSignIn(fsType)) {
      // not connected in this session: sign in, then go to the folder
      let remove = showMessage("connecting " + signInFsName(fsType));
      try {
        newFS = await connectBySignIn(fsType);
      } catch (e) {
        console.error(e);
        remove();
        showTemporaryMessage((e as Error).message || String(e));
        return;
      }
      remove();
    }
    let savedLocal = newFS ? null : findBookmarkSavedLocal(b);
    if (savedLocal) {
      // the browser asks the user to let us use the folder again
      try {
        newFS = await openSavedLocalFolder(savedLocal);
      } catch (e) {
        console.error(e);
        showTemporaryMessage((e as Error).message || String(e));
        return;
      }
    }
    if (!newFS) {
      showTemporaryMessage("The connection of this bookmark is not connected or saved any more");
      return;
    }
    rememberFileSystem(newFS);
    let newFSState = new FSDirState(newFS, b.dir);
    navVisited(num, newFS, b.dir);

    // if not mounted, set the directory with fs1Dir/fs2Dir
    // if mounted, set the directory with tryReadDir()
    if (num == 1) {
      let same = fs1State && fsEq(fs1State.fs, newFS);
      if (!same) {
        fs1State = newFSState;
      } else {
        fs1Component?.tryReadDir(b.dir);
      }
    } else if (num == 2) {
      let same = fs2State && fsEq(fs2State.fs, newFS);
      if (!same) {
        fs2State = newFSState;
      } else {
        fs2Component?.tryReadDir(b.dir);
      }
    } else {
      throwIf(true, `num is $[num}, should be 1 or 2`);
    }
    // let sameDir = sameFS && dir == currFs.dir;
    // captureEvent("gotobookmark", { type: b.fsID });
  }

  async function mountfs(fs: import("./fs").FS, num: number) {
    console.log("mountfs:", fs, num);

    rememberFileSystem(fs);
    let fsState = new FSDirState(fs, "/");
    navVisited(num, fs, "/");

    if (num == 1) {
      fs1State = fsState;
    } else if (num == 2) {
      fs2State = fsState;
    } else {
      throw "invalid num";
    }

    captureEvent("mountfs", { type: fs.type });
  }

  // Closing the panel is remembered in this browser. The key is not the one
  // of the small notice the panel replaced, so that those who closed the
  // notice see the panel once.
  const lsKeyInfoVisible = "fm-intro-closed";
  let infoVisible = $state(!localStorage.getItem(lsKeyInfoVisible));
  function hideInfo() {
    infoVisible = false;
    localStorage.setItem(lsKeyInfoVisible, "no");
  }
  // @ts-ignore
  window.showInfo = function () {
    localStorage.removeItem(lsKeyInfoVisible);
    infoVisible = true;
  };

  // ---- the command palette

  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || "");

  let paletteOpen = $state(false);
  let paletteQuery = $state("");
  let paletteLists: PaletteLists = $state({ commands: [], history: [], favorites: [] });
  // the pane the palette works on: the one that had the focus
  let palettePane = 1;
  let paletteFocus: HTMLElement | null = null;

  function paneState(num: number): FSDirState | undefined {
    return num == 1 ? fs1State : fs2State;
  }

  function paletteCommands(num: number): PaletteItem[] {
    let res: PaletteItem[] = [];
    let state = paneState(num);
    let component = num == 1 ? fs1Component : fs2Component;
    if (state && component) {
      for (let mi of component.getCommands()) {
        let handler = mi.handler;
        if (mi.isSeparator() || mi.isSubMenu() || !handler) {
          continue;
        }
        res.push({ label: htmlLabelToText(mi.label), html: mi.label, hint: mi.shortcut, run: handler });
      }
      res.push({ label: "Filter Files", hint: "/", run: () => component.focusFilter() });
      let b = bookmarkForFS(state.fs, state.currDir);
      if (!bookmarkExists(b)) {
        res.push({ label: "Add This Folder To Favorites", run: () => addBookmark(b) });
      }
    }
    let nav = num == 1 ? nav1 : nav2;
    let goback = nav.goback;
    if (goback) {
      res.push({ label: "Go Back", hint: "Alt + Left", run: goback });
    }
    let goforward = nav.goforward;
    if (goforward) {
      res.push({ label: "Go Forward", hint: "Alt + Right", run: goforward });
    }
    if (state) {
      res.push({ label: "Go To Home View", run: () => showTopLevel(num) });
    }
    res.push({ label: "Open Manual", run: () => window.open("/doc/Manual", "_blank") });
    return res;
  }

  // the places the pane has shown, newest first, each once
  function paletteHistory(num: number): PaletteItem[] {
    let res: PaletteItem[] = [];
    let h = paneHistory(num);
    let seen: NavLoc[] = [];
    for (let i = h.entries.length - 1; i >= 0; i--) {
      let loc = h.entries[i];
      if (seen.some((l) => navLocEq(l, loc))) {
        continue;
      }
      seen.push(loc);
      res.push({
        label: loc.fs ? fsFullDir(loc.fs, loc.dir) : "Home View",
        hint: i == h.idx ? "current" : "",
        run: () => goToLoc(num, loc),
      });
    }
    return res;
  }

  function paletteFavorites(num: number): PaletteItem[] {
    return bookmarks.map((b) => ({
      label: bookmarkLabel(b),
      run: () => goToBookmark(b, num),
      remove: () => removeBookmark(b),
    }));
  }

  // goes to a place as to a new one
  function goToLoc(num: number, loc: NavLoc) {
    if (!loc.fs) {
      showTopLevel(num);
      return;
    }
    navVisited(num, loc.fs, loc.dir);
    navShow(num, loc);
  }

  function openPalette(query: string) {
    let active = document.activeElement as HTMLElement | null;
    let pane = active?.closest("[data-pane]") || document.querySelector("[data-pane]");
    palettePane = pane?.getAttribute("data-pane") == "2" ? 2 : 1;
    paletteFocus = active;
    paletteLists = {
      commands: paletteCommands(palettePane),
      history: paletteHistory(palettePane),
      favorites: paletteFavorites(palettePane),
    };
    paletteQuery = query;
    paletteOpen = true;
  }

  function closePalette() {
    paletteOpen = false;
    // the keys go back to where they were
    if (paletteFocus?.isConnected) {
      paletteFocus.focus();
    }
    paletteFocus = null;
  }

  function runPaletteItem(item: PaletteItem) {
    closePalette();
    item.run();
  }

  function removePaletteItem(item: PaletteItem) {
    item.remove?.();
    paletteLists.favorites = paletteFavorites(palettePane);
  }

  // Ctrl + K, or Cmd + K on a Mac, shows and hides the palette. ">", "#"
  // and "$" typed outside of a text box show it on their list. Before
  // anyone else sees the key: the file list has its own use for "k".
  function onWindowKeyDown(ev: KeyboardEvent) {
    let isShortcut = isPaletteShortcut(ev, isMac);
    if (paletteOpen) {
      if (isShortcut) {
        ev.preventDefault();
        ev.stopPropagation();
        closePalette();
      }
      return;
    }
    let prefix = "";
    if (!isShortcut) {
      if (ev.ctrlKey || ev.metaKey || ev.altKey) {
        return;
      }
      prefix = palettePrefixOfKey(ev.key);
      let el = ev.target as HTMLElement | null;
      if (prefix == "" || !el || el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
        return;
      }
    }
    // not over a dialog or a menu
    if (document.querySelector('[role="dialog"], .fm-overlay, .context-menu')) {
      return;
    }
    // Ctrl + K is the browser's too
    ev.preventDefault();
    ev.stopPropagation();
    openPalette(prefix);
  }

  $effect(() => {
    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => window.removeEventListener("keydown", onWindowKeyDown, true);
  });

  let errorMsg = $state("");
  function showError(ev: CustomEvent) {
    let err = ev.detail;
    errorMsg = err.toString();
  }
  function hideErrorMessage() {
    errorMsg = "";
  }

  function pickOtherFSState(fsState: FSDirState): FSDirState {
    if (fsState == fs1State) {
      return fs2State as FSDirState;
    }
    if (fsState == fs2State) {
      return fs1State as FSDirState;
    }
    throw new Error("pickOtherFSState: unrecognized fsState");
  }
  run(() => {
    if (!fs1State || !fs2State) {
      $canCopyMove = false;
      return;
    }
    $canCopyMove = calcCanCopyMove(fs1State, fs2State);
  });
</script>

<main
  class="fixed inset-0 p-0 m-0 grid"
  oncontextmenu={preventDefault(() => {
    /* do nothing */
  })}
>
  <!-- big screen: show 2 panels; small screen: 1 panel -->
  {#if $viewportWidth > 768}
    <div class="grid min-h-0 grid-cols-2">
      <div class="contents" data-pane="1">
        {#key fs1State}
          {#if fs1State}
            <FileList
              {pickOtherFSState}
              fsState={fs1State}
              bind:this={fs1Component}
              gotobookmark={(b) => goToBookmark(b, 1)}
              showtoplevel={() => showTopLevel(1)}
              nav={nav1}
              ondirshown={(dir) => navVisited(1, fs1State?.fs ?? null, dir)}
            />
          {:else}
            <PickFileSystem
              takefocus={focusHomeView == 1}
              nav={nav1}
              gotobookmark={(b) => goToBookmark(b, 1)}
              mountfs={(fs) => mountfs(fs, 1)}
            />
          {/if}
        {/key}
      </div>
      <div class="contents" data-pane="2">
        {#key fs2State}
          {#if fs2State}
            <FileList
              {pickOtherFSState}
              fsState={fs2State}
              bind:this={fs2Component}
              gotobookmark={(b) => goToBookmark(b, 2)}
              showtoplevel={() => showTopLevel(2)}
              nav={nav2}
              ondirshown={(dir) => navVisited(2, fs2State?.fs ?? null, dir)}
            />
          {:else}
            <PickFileSystem
              takefocus={focusHomeView == 2}
              nav={nav2}
              gotobookmark={(b) => goToBookmark(b, 2)}
              mountfs={(fs) => mountfs(fs, 2)}
            />
          {/if}
        {/key}
      </div>
    </div>
  {:else}
    <div class="grid min-h-0 grid-cols-1" data-pane={fs1State || !fs2State ? "1" : "2"}>
      {#if fs1State}
        {#key fs1State}
          <FileList
            {pickOtherFSState}
            fsState={fs1State}
            bind:this={fs1Component}
            gotobookmark={(b) => goToBookmark(b, 1)}
            showtoplevel={() => showTopLevel(1)}
            nav={nav1}
            ondirshown={(dir) => navVisited(1, fs1State?.fs ?? null, dir)}
          />
        {/key}
      {:else if fs2State}
        {#key fs2State}
          <FileList
            {pickOtherFSState}
            fsState={fs2State}
            bind:this={fs2Component}
            gotobookmark={(b) => goToBookmark(b, 2)}
            showtoplevel={() => showTopLevel(2)}
            nav={nav2}
            ondirshown={(dir) => navVisited(2, fs2State?.fs ?? null, dir)}
          />
        {/key}
      {:else}
        <PickFileSystem
          takefocus={focusHomeView == 1}
          nav={nav1}
          gotobookmark={(b) => goToBookmark(b, 1)}
          mountfs={(fs) => mountfs(fs, 1)}
        />
      {/if}
    </div>
  {/if}

  {#if paletteOpen}
    <CommandPalette
      initialQuery={paletteQuery}
      lists={paletteLists}
      onclose={closePalette}
      onrun={runPaletteItem}
      onremove={removePaletteItem}
    />
  {/if}

  <!-- what the program is, for someone new to it, until it is closed -->
  {#if infoVisible}
    <div class="relative border-t-2 border-yellow-400 bg-yellow-100 pl-6 pr-12 py-5 text-base leading-relaxed">
      Sumatra File Manager Online is a web-based file manager for online storage services like S3, DropBox and
      <a href="/doc/Manual" target="_blank" class="text-blue-700 underline underline-offset-2">many more</a>.
      Double-click to add a connection.
      <a href="/doc/Getting-started" target="_blank" class="text-blue-700 underline underline-offset-2">Learn more</a>.
      <button
        class="absolute top-2 right-2 p-1 hover:bg-yellow-300 cursor-pointer"
        aria-label="Close"
        title="Close"
        onclick={hideInfo}
      >
        <Close size="20" />
      </button>
    </div>
  {/if}

  {#if errorMsg}
    <div
      class="absolute bottom-0 left-0 ml-2 mt-1 flex flex-row justify-between items-center px-3 py-1 mb-2 text-red-500 font-semibold bg-red-100 border border-red-300 rounded-lg"
    >
      <div class="break-all">{errorMsg}</div>
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <button class="self-center mt-1 ml-2 hover:bg-red-200 cursor-pointer" onclick={hideErrorMessage}>
        <Close />
      </button>
    </div>
  {/if}

  {#if $currentMessage}
    <div class="absolute bottom-0 right-0 px-2 py-0 bg-yellow-200 z-10">
      {$currentMessage}
    </div>
  {/if}
</main>

<Tooltip />

<style lang="postcss">
  /* the panes, then the panel that says what the program is */
  main {
    grid-template-rows: 1fr auto;
  }
</style>
